<template>
  <Dialog v-model:visible="visible" :modal="true" :draggable="false" :closable="false" :showHeader="false"
    style="width: min(340px, 88vw)" class="app-confirm-dialog">
    <div class="flex flex-column align-items-center justify-content-center p-4 text-center">
      <img :src="iconSrc" alt="" class="app-confirm-dialog__icon" />
      <span class="font-bold text-xl block mb-2 mt-3 gem-main-heading">{{
        displayHeader
      }}</span>
      <p v-if="dialog.message" class="mb-0 px-1 line-height-3">
        {{ dialog.message }}
      </p>
      <div v-if="dialog.shareLink" class="share-link-row">
        <a :href="dialog.shareLink" target="_blank" rel="noopener noreferrer" class="share-link">{{ dialog.shareLink
          }}</a>
        <button type="button" class="share-link-copy" v-tooltip.bottom="'Copy this link'" aria-label="Copy this link"
          @click="copyShareLink">
          <Icon type="favicon" :icon="AppIcons.COPY" class="share-link-copy-icon" />
        </button>
      </div>
      <div v-if="dialog.requireReason" class="w-full">
        <label class="block mb-1 text-sm text-left"
        :class="{ required: !!dialog.reasonRequired, 'mt-3': !dialog.reasonRequired }">
          Reason
        </label>
        <base-text-area v-model="reason" name="reason" placeholder="Enter your reason" :rows="3"
          :required="!!dialog.reasonRequired" />
      </div>
      <div v-if="dialog.requireLoginOption" class="flex align-items-center justify-content-between mt-3"
        style="width: 60%">
        <span>Require user logs in?</span>
        <div class="flex align-items-center justify-content-center gap-2">
          <Checkbox v-model="requireLogin" binary input-id="app-confirm-require-login" />
          <span>Yes</span>
        </div>
      </div>
      <div class="flex align-items-center justify-content-center gap-2 mt-4">
        <Button v-if="showReject" type="button" class="file-listing-btns p-button-sm" @click="close('reject')">
          <span class="save-action text-center">
            <Icon type="favicon" :icon="AppIcons.CROSS" class="" />
            <span>{{ dialog.rejectLabel || $t("no") }}</span>
          </span>
        </Button>
        <Button type="button" class="file-listing-btns p-button-sm" @click="close('accept')">
          <span class="save-action text-center">
            <Icon type="favicon" :icon="acceptIcon" class="" />
            <span>{{ acceptLabel }}</span>
          </span>
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script>
import confirmIcon from "@/assets/images/icons/128x128/Question.png";
import helpIcon from "@/assets/images/icons/128x128/Info.png";
import warningIcon from "@/assets/images/icons/128x128/Warning.png";
import shareIcon from "@/assets/images/icons/128x128/Share.png";
import { AppIcons } from "../../constants/app-icons";
import { useForm } from "vee-validate";

export default {
  setup() {
    const confirmForm = useForm();
    return { confirmForm };
  },
  provide() {
    return {
      meta: { mode: "edit" },
    };
  },
  data() {
    return {
      AppIcons,
      visible: false,
      type: null,
      dialog: {},
      requireLogin: false,
      reason: "",
      confirmIcon,
      helpIcon,
      warningIcon,
      shareIcon,
    };
  },
  computed: {
    typeConfig() {
      const map = {
        confirm: {
          icon: this.confirmIcon,
          defaultHeader: "Confirmation",
          showReject: true,
          acceptIcon: AppIcons.SAVE_TICK,
          acceptLabel: null,
        },
        warning: {
          icon: this.warningIcon,
          defaultHeader: "Warning",
          showReject: false,
          acceptIcon: AppIcons.ENABLED,
          acceptLabel: "OK",
        },
        error: {
          icon: this.warningIcon,
          defaultHeader: "Error",
          showReject: false,
          acceptIcon: AppIcons.ENABLED,
          acceptLabel: "OK",
        },
        info: {
          icon: this.helpIcon,
          defaultHeader: "Information",
          showReject: false,
          acceptIcon: AppIcons.ENABLED,
          acceptLabel: "OK",
        },
        share: {
          icon: this.shareIcon,
          defaultHeader: "Share File",
          showReject: true,
          acceptIcon: AppIcons.HAND_WITH_APPLICATION,
          acceptLabel: "Share",
        },
      };
      return map[this.type] || map.confirm;
    },
    iconSrc() {
      return this.typeConfig.icon;
    },
    displayHeader() {
      return this.dialog.header || this.typeConfig.defaultHeader;
    },
    showReject() {
      if (this.dialog.showReject === false) return false;
      if (this.dialog.showReject === true) return true;
      return this.typeConfig.showReject;
    },
    acceptLabel() {
      return (
        this.dialog.acceptLabel || this.typeConfig.acceptLabel || this.$t("yes")
      );
    },
    acceptIcon() {
      return this.dialog.acceptIcon || this.typeConfig.acceptIcon;
    },
  },
  mounted() {
    this._openDialog = (payload) => this.open(payload);
    this.emitter.on("open-dialog", this._openDialog);
  },
  beforeUnmount() {
    this.emitter.off("open-dialog", this._openDialog);
  },
  methods: {
    open(payload) {
      if (this.visible) return;
      this.type = payload.dialogType || "confirm";
      this.dialog = payload;
      this.requireLogin = false;
      this.confirmForm?.resetForm();
      this.reason = "";
      this.visible = true;
    },
    finishClose() {
      if (this.dialog?.service) this.dialog.service.formAnchor = null;
      this.visible = false;
      this.type = null;
      this.dialog = {};
      this.requireLogin = false;
      this.reason = "";
    },
    close(action) {
      const svc = this.dialog?.service;
      if (action === "accept") {
        if (svc) svc.formAnchor = this.confirmForm;
        const reason = (this.reason || "").trim();

        let result;
        if (this.dialog.requireLoginOption) {
          result = this.dialog.accept?.({
            requireLogin: this.requireLogin,
            reason,
          });
        } else if (this.dialog.requireReason) {
          result = this.dialog.accept?.({ reason });
        } else {
          result = this.dialog.accept?.();
        }
        if (result && typeof result.then === "function") {
          result.then(() => this.finishClose()).catch((err) => {
            const msg = err?.errors?.message;
            if (msg) this.confirmForm.setErrors({ reason: msg });
          });
          return;
        }
      } else if (action === "reject") {
        this.dialog.reject?.();
      }
      this.finishClose();
    },
    copyShareLink() {
      const text = this.dialog.shareLink;
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.add({
          severity: "success",
          summary: "Copied",
          detail: "Link copied to clipboard.",
          life: 2000,
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.app-confirm-dialog__icon {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.share-link-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  max-width: 100%;
  margin-top: 0.75rem;
}

.share-link {
  color: #2196f3;
  text-decoration: underline;
  word-break: break-all;
  text-align: center;
}

.share-link-copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
}

.share-link-copy-icon {
  width: 16px;
  height: 16px;
}
</style>
